import { useEffect, useState } from "react"
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom"
import { Globe, Mail } from "lucide-react"
import { isValidPhoneNumber } from "react-phone-number-input"
import { toast } from "sonner"

import {
  AuthCard,
  AuthLegalNotice,
  AuthPageHeader,
  authButtonClass,
  authFieldClass,
  authNoticeClass,
  authOutlineButtonClass,
} from "@/components/auth/AuthCard"
import { AuthLayout } from "@/components/layouts/AuthLayout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { PhoneInput } from "@/components/ui/phone-input"
import { FullPageLoader } from "@/components/ui/full-page-loader"
import { useAuth } from "@/contexts/AuthContext"
import { useAppSession } from "@/hooks/useAppSession"
import {
  getValidStoreHandle,
  persistClaimedStoreUrl,
  readClaimedStoreUrl,
  readPendingBusinessData,
  writePendingBusinessData,
} from "@/utils/storeUrlValidator"

export function RegisterPage() {
  const [searchParams] = useSearchParams()
  const [step, setStep] = useState<"details" | "verify">("details")
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    businessName: "",
    email: "",
    whatsappNumber: "",
  })
  const [phoneError, setPhoneError] = useState<string>("")
  const [code, setCode] = useState("")
  const [claimedHandle, setClaimedHandle] = useState("")
  const { requestMagicLink, verifyMagicLink } = useAuth()
  const navigate = useNavigate()
  const { signedIn, isLoading: isAuthLoading, appPath } = useAppSession()

  useEffect(() => {
    const fromQuery = getValidStoreHandle(searchParams.get("store") || "")
    const stored = persistClaimedStoreUrl(fromQuery || readClaimedStoreUrl())
    setClaimedHandle(stored)
    if (stored) writePendingBusinessData({ url: stored })
  }, [searchParams])

  const handleDetailsSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.whatsappNumber) {
      setPhoneError("Phone number is required")
      return
    }

    if (!isValidPhoneNumber(formData.whatsappNumber)) {
      setPhoneError("Please enter a valid phone number")
      return
    }

    setPhoneError("")
    setIsLoading(true)

    try {
      writePendingBusinessData({
        name: formData.businessName,
        whatsappNumber: formData.whatsappNumber,
        ...(claimedHandle ? { url: claimedHandle } : {}),
      })

      await requestMagicLink(formData.email)
      toast.success("Verification code sent to your email")
      setStep("verify")
    } catch {
      toast.error("Failed to send verification code. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifySubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)

    try {
      const businessData = readPendingBusinessData()

      await verifyMagicLink(formData.email, code, businessData)
      toast.success("Account created successfully")
      navigate("/onboarding")
    } catch {
      toast.error("Invalid or expired code. Please try again.")
    } finally {
      setIsLoading(false)
    }
  }

  if (isAuthLoading) {
    return <FullPageLoader text="Signing you in" />
  }

  if (signedIn) {
    return <Navigate to={appPath} replace />
  }

  return (
    <AuthLayout>
      <AuthCard>
        <AuthPageHeader
          align="center"
          eyebrow="Get started"
          title="Create your store"
          description={
            claimedHandle
              ? `Finish signup to claim sqale.shop/${claimedHandle}`
              : "Get started with your online store in minutes"
          }
        />

        {claimedHandle ? (
          <div className={authNoticeClass}>
            <Globe className="size-4 shrink-0 text-primary" />
            <p>
              You&apos;re claiming{" "}
              <strong className="text-primary">
                sqale.shop/{claimedHandle}
              </strong>
            </p>
          </div>
        ) : null}

        {step === "details" ? (
          <form onSubmit={handleDetailsSubmit} className="space-y-4">
            <Input
              type="text"
              placeholder="Full Name"
              value={formData.businessName}
              className={authFieldClass}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  businessName: e.target.value,
                }))
              }
              required
            />
            <Input
              type="email"
              placeholder="Email address"
              value={formData.email}
              className={authFieldClass}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, email: e.target.value }))
              }
              required
            />
            <div className="space-y-2">
              <PhoneInput
                international
                defaultCountry="NG"
                value={formData.whatsappNumber}
                size="lg"
                onChange={(value) => {
                  setFormData((prev) => ({
                    ...prev,
                    whatsappNumber: value || "",
                  }))
                  setPhoneError("")
                }}
                placeholder="WhatsApp number"
                required
                error={phoneError}
              />
              {phoneError ? (
                <p className="text-sm text-destructive">{phoneError}</p>
              ) : null}
            </div>
            <Button
              type="submit"
              className={authButtonClass}
              disabled={isLoading}
            >
              {isLoading ? "Creating account..." : "Continue"}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleVerifySubmit} className="space-y-4">
            <div className={authNoticeClass}>
              <Mail className="size-4 shrink-0 text-primary" />
              <p>
                We&apos;ve sent a 6-digit code to{" "}
                <strong>{formData.email}</strong>
              </p>
            </div>
            <Input
              type="text"
              placeholder="Enter 6-digit code"
              value={code}
              className={authFieldClass}
              onChange={(e) => setCode(e.target.value)}
              maxLength={6}
              required
            />
            <Button
              type="submit"
              className={authButtonClass}
              disabled={isLoading}
            >
              {isLoading ? "Verifying..." : "Create Store"}
            </Button>
            <Button
              type="button"
              variant="outline"
              className={authOutlineButtonClass}
              onClick={() => setStep("details")}
            >
              Edit details
            </Button>
          </form>
        )}

        <AuthLegalNotice action="create" />

        <div className="mt-5 text-center text-sm font-medium text-muted-foreground">
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-semibold text-primary underline-offset-4 hover:underline"
          >
            Sign in
          </Link>
        </div>
      </AuthCard>
    </AuthLayout>
  )
}
